<script setup>
/* Push-Einstellungen: Liste aller Ereignisse mit An/Aus-Schalter.
 *
 * Steuert NUR den Push aufs Gerät. Die Glocke in der App zeigt weiterhin alle
 * Meldungen — das steht im Dialog, damit niemand glaubt, ein ausgeschalteter
 * Schalter blende die Meldung aus.
 *
 * Kein Eintrag bedeutet "an". Geprüft wird beim Versand in der Edge Function
 * notify, nicht hier; diese Oberfläche legt nur die Einstellung ab.
 */

import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import OverlayModal from './OverlayModal.vue';
import { usePushPreferences } from '../composables/usePushPreferences.js';
import { useNotifications } from '../composables/useNotifications.js';
import { useAuth } from '../composables/useAuth.js';
import { settingsTypesFor } from '../domain/notifications.js';

const open = defineModel({ type: Boolean, default: false });

const { t } = useI18n();
const { loading, loaded, error, load, isEnabled, setEnabled } = usePushPreferences();
const { pushAvailable, pushActive } = useNotifications();
const { role } = useAuth();

watch(open, (isOpen) => {
  if (isOpen) load();
});

function onToggle(type, event) {
  setEnabled(type, event.target.checked);
}
</script>

<template>
  <OverlayModal v-if="open" :title="t('pushSettings.title')" :width="460" @close="open = false">
    <p class="ps-intro">{{ $t('pushSettings.intro') }}</p>

    <p v-if="pushAvailable && !pushActive" class="ps-device">{{ $t('pushSettings.deviceHint') }}</p>

    <p v-if="loading && !loaded" class="ps-status">{{ $t('common.loading') }}</p>
    <p v-else-if="error && !loaded" class="ps-error">{{ $t(`errors.${error}`) }}</p>

    <ul v-if="loaded" class="ps-list">
      <li v-for="type in settingsTypesFor(role)" :key="type" class="ps-row">
        <label class="ps-label" :for="`ps-${type}`">{{ $t(`pushSettings.types.${type}`) }}</label>
        <input
          :id="`ps-${type}`"
          class="ps-switch"
          type="checkbox"
          role="switch"
          :checked="isEnabled(type)"
          :aria-label="$t(`pushSettings.types.${type}`)"
          @change="onToggle(type, $event)"
        />
      </li>
    </ul>

    <p v-if="loaded && error" class="ps-error">{{ $t(`errors.${error}`) }}</p>

    <p class="ps-note">{{ $t('pushSettings.bellNote') }}</p>
    <p class="ps-note">{{ $t('pushSettings.selfNote') }}</p>

    <template #actions>
      <button class="btn ghost small" @click="open = false">{{ $t('common.close') }}</button>
    </template>
  </OverlayModal>
</template>

<style scoped>
.ps-intro {
  margin: 0 0 14px;
  color: var(--muted);
  font-size: 0.85rem;
  line-height: 1.5;
}
.ps-device {
  margin: 0 0 14px;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--amber);
  font-size: 0.8rem;
}
.ps-status {
  color: var(--muted);
  font-size: 0.85rem;
}
.ps-error {
  color: var(--red);
  font-size: 0.8rem;
}
.ps-list {
  margin: 0 0 14px;
  padding: 0;
  list-style: none;
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
}
.ps-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 11px 14px;
  background: var(--card);
}
.ps-row + .ps-row {
  border-top: 1px solid var(--border);
}
.ps-label {
  flex: 1;
  font-size: 0.86rem;
  color: var(--text);
  cursor: pointer;
}
.ps-note {
  margin: 0 0 6px;
  color: var(--dim);
  font-size: 0.75rem;
  line-height: 1.45;
}

/* Schalter aus einem Kontrollkästchen: kein Bibliothekselement nötig, nutzt
   die Farben des Designs. */
.ps-switch {
  appearance: none;
  flex: none;
  position: relative;
  width: 38px;
  height: 22px;
  margin: 0;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--panel);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.ps-switch::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--muted);
  transition: transform 0.15s, background 0.15s;
}
.ps-switch:checked {
  background: color-mix(in srgb, var(--green) 25%, var(--panel));
  border-color: var(--green-dim);
}
.ps-switch:checked::after {
  transform: translateX(16px);
  background: var(--green);
}
.ps-switch:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: 2px;
}
</style>
